/**
 * Decide how a cart box is paid.
 *
 * One live listing stays on today's default-purchase (stock on the payment
 * transaction). Two or more live listings use r2r-cart-purchase plus a child
 * reservation per listing. Sold rows stay in the cart; they are never charged.
 */

import { isDeadRow, partitionCartRows } from './CartPage.shared';

const toInitiateRow = row => ({
  listingId: row.listingId,
  qty: row.qty,
  deliveryMethod: row.deliveryMethod,
});

const cartItemsFromLiveRows = rows =>
  (Array.isArray(rows) ? rows : []).map(row => ({
    listingId: row.listingId,
    title: row.title || null,
    qty: row.qty,
    imageUrl: row.imageUrl || null,
  }));

/**
 * Heading for checkout: every listing name in the box, or the attached listing.
 *
 * @param {Object|null|undefined} orderData
 * @param {Object|null|undefined} listing
 * @returns {string|undefined}
 */
export const checkoutListingTitle = (orderData, listing) => {
  const titles = (orderData?.cartItems || []).map(item => item?.title).filter(Boolean);
  if (titles.length > 0) {
    return titles.join(', ');
  }
  return listing?.attributes?.title;
};

const liveListingIds = rows =>
  (Array.isArray(rows) ? rows : [])
    .filter(r => r?.listingId && !isDeadRow(r))
    .map(r => r.listingId)
    .sort();

/**
 * @param {Object|null|undefined} box partitioned cart box
 * @returns {{ kind: 'blocked' }|{ kind: 'single', listingId: string, orderData: Object }|{ kind: 'box', listingId: string, orderData: Object }}
 */
export const planBoxCheckout = box => {
  const liveRows = Array.isArray(box?.liveRows)
    ? box.liveRows.filter(r => r?.listingId && !isDeadRow(r))
    : [];

  if (liveRows.length === 0) {
    return { kind: 'blocked' };
  }

  if (liveRows.length === 1) {
    const row = liveRows[0];
    return {
      kind: 'single',
      listingId: row.listingId,
      orderData: {
        quantity: row.qty,
        deliveryMethod: row.deliveryMethod,
        fromCart: true,
        cartItems: cartItemsFromLiveRows(liveRows),
      },
    };
  }

  return {
    kind: 'box',
    listingId: liveRows[0].listingId,
    orderData: {
      deliveryMethod: box.deliveryMethod || liveRows[0].deliveryMethod,
      fromCart: true,
      cartRows: liveRows.map(toInitiateRow),
      cartItems: cartItemsFromLiveRows(liveRows),
    },
  };
};

/**
 * CheckoutPage orderData for a 2+ listing box carries `cartRows`. A one-listing
 * checkout looks like today's listing-page payload and must not take this path.
 *
 * @param {Object|null|undefined} orderParams
 * @returns {boolean}
 */
export const isMultiListingCartCheckout = orderParams =>
  Array.isArray(orderParams?.cartRows) && orderParams.cartRows.length >= 2;

/**
 * A 2+ listing box must not send stockReservationQuantity on the payment
 * transaction — that process has no stock actions. Quantity lives on cartRows.
 *
 * @param {Object} orderParams
 * @param {Object|null|undefined} orderData
 * @returns {Object}
 */
export const attachCartRowsToOrderParams = (orderParams, orderData) => {
  if (!isMultiListingCartCheckout(orderData)) {
    return orderParams;
  }
  const { quantity, ...rest } = orderParams;
  return {
    ...rest,
    cartRows: orderData.cartRows,
  };
};

/**
 * Body for POST /api/initiate-cart-privileged, or null when this order is a
 * one-listing default-purchase checkout.
 *
 * @param {Object} params
 * @returns {Object|null}
 */
export const cartPrivilegedInitiateBody = ({
  isSpeculative,
  orderParams,
  bodyParams,
  queryParams,
}) => {
  if (!isMultiListingCartCheckout(orderParams)) {
    return null;
  }
  return {
    isSpeculative,
    rows: orderParams.cartRows,
    bodyParams,
    queryParams,
  };
};

/**
 * Listing ids to drop from the cart after this order is paid. Empty when
 * checkout started from a listing page, so booking/inquiry are untouched.
 *
 * @param {Object|null|undefined} orderData
 * @param {Object|null|undefined} listing
 * @returns {Array<string>}
 */
export const listingIdsPaidFromCart = (orderData, listing) => {
  if (!orderData?.fromCart) {
    return [];
  }
  if (isMultiListingCartCheckout(orderData)) {
    return orderData.cartRows.map(r => r.listingId);
  }
  const id = listing?.id?.uuid || listing?.id;
  return id ? [id] : [];
};

/**
 * Rows to revalidate immediately before charging. Null when this is not a cart checkout.
 *
 * @param {Object|null|undefined} orderData
 * @param {Object|null|undefined} listing
 * @returns {Array<Object>|null}
 */
export const checkoutRowsForQuote = (orderData, listing) => {
  if (!orderData?.fromCart) {
    return null;
  }
  if (isMultiListingCartCheckout(orderData)) {
    return orderData.cartRows;
  }
  const listingId = listing?.id?.uuid || listing?.id;
  if (!listingId) {
    return null;
  }
  return [
    {
      listingId,
      qty: orderData.quantity,
      deliveryMethod: orderData.deliveryMethod,
    },
  ];
};

/**
 * True when the set of live listing ids changed between the click and the
 * just-run quote. A price move is not a roster change; a death is.
 *
 * @param {Array<{ listingId: string }>} intendedRows
 * @param {Array<Object>} quotedRows
 * @returns {boolean}
 */
export const didCheckoutRosterChange = (intendedRows, quotedRows) => {
  const intended = liveListingIds(intendedRows);
  const quoted = liveListingIds(quotedRows);
  if (intended.length !== quoted.length) {
    return true;
  }
  return intended.some((id, i) => id !== quoted[i]);
};

/**
 * Revalidate, refuse to charge if the live roster moved, then load the listing
 * CheckoutPage needs as its attached listing.
 *
 * @param {Object} params
 * @param {Object} params.box
 * @param {Array<Object>} params.cartRows persisted cart rows
 * @param {Function} params.revalidateCart
 * @param {Function} params.fetchListing
 * @returns {Promise<{ ok: true, listing: Object, orderData: Object }|{ ok: false, reason: string }>}
 */
export const prepareBoxCheckout = async ({ box, cartRows, revalidateCart, fetchListing }) => {
  const intendedLive = (box?.liveRows || []).filter(r => r?.listingId && !isDeadRow(r));
  if (intendedLive.length === 0) {
    return { ok: false, reason: 'empty' };
  }

  const quoted = await revalidateCart(cartRows);
  const { boxes } = partitionCartRows({
    rows: quoted?.rows,
    groups: quoted?.groups,
  });
  const fresh = boxes.find(b => b.key === box.key);

  const quotedLive = fresh?.liveRows || [];
  if (!fresh || didCheckoutRosterChange(intendedLive, quotedLive)) {
    return { ok: false, reason: 'changed' };
  }

  const plan = planBoxCheckout(fresh);
  if (plan.kind === 'blocked') {
    return { ok: false, reason: 'empty' };
  }

  const listing = await fetchListing(plan.listingId);
  if (!listing) {
    return { ok: false, reason: 'listing' };
  }

  return { ok: true, listing, orderData: plan.orderData };
};
